import { EVENTS } from "../const"
import { openContactEmail, openContactTelegram } from "../utils/contactLinks"
import { cn } from "../utils/tailwindcss"
import { AnimatedText } from "./AnimatedText"
import { Grid } from "./Grid"

type MenuProps = {
  isOpen?: boolean
  onCloseClick?: () => void
  /** When provided, menu item clicks call this with the text DOM element (e.g. to trigger experience events). */
  onItemClick?: (element: HTMLElement) => void
}

export function Menu({ isOpen = false, onCloseClick, onItemClick }: MenuProps) {
  const handleItemClick = (e: React.MouseEvent) => {
    const textEl = (e.target as HTMLElement).closest(
      '[data-animation="webgl-text"]',
    ) as HTMLElement | null
    const cta = textEl?.getAttribute("data-contact-cta")
    if (cta === "email") {
      openContactEmail()
      return
    }
    if (cta === "telegram") {
      openContactTelegram()
      return
    }
    if (textEl && onItemClick) {
      onItemClick(textEl)
    }
  }

  return (
    <div
      data-name="menu"
      className="fixed z-50 w-full h-screen top-0 left-0 pointer-events-none"
    >
      <div
        role="button"
        tabIndex={0}
        className={`absolute top-2 left-auto right-4 w-15 text-right cursor-pointer ${isOpen ? "pointer-events-auto" : "invisible pointer-events-none"}`}
        onClick={onCloseClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            onCloseClick?.()
          }
        }}
      >
        <AnimatedText
          className="text-[14px]"
          preventUpdate
          content="CLOSE"
          data-click-event={EVENTS.Menu.click}
          data-hover-event={EVENTS.Menu.hover}
          data-out-event={EVENTS.Menu.out}
          data-reveal-duration="0.6"
        />
      </div>
      <Grid>
        <div className="col-start-2 col-span-2 lg:col-span-3 lg:col-start-9 xl:col-span-2 xl:col-start-10 text-right md:text-left leading-none pt-20">
          <ul className="text-left space-y-1">
            <li
              className={cn(
                "flex items-center gap-2",
                isOpen ? "pointer-events-auto cursor-default" : undefined,
              )}
              onClick={handleItemClick}
            >
              <AnimatedText
                preventUpdate
                data-font-bold
                content="["
                data-reveal-duration="0.2"
                data-click-event={EVENTS.Menu.itemClick}
                data-section-target="loader"
                className="text-gray-600"
              />
              <AnimatedText
                className="cursor-pointer text-gray-400"
                preventUpdate
                data-font-bold
                content="About"
                data-reveal-duration="0.4"
                data-click-event={EVENTS.Menu.itemClick}
                data-hover-event={EVENTS.Menu.hover}
                data-out-event={EVENTS.Menu.out}
                data-section-target="loader"
              />
            </li>
            <li
              className={cn(
                "flex items-center gap-2",
                isOpen ? "pointer-events-auto cursor-default" : undefined,
              )}
              onClick={handleItemClick}
            >
              <AnimatedText
                preventUpdate
                data-font-bold
                content="|"
                data-reveal-duration="0.2"
                data-click-event={EVENTS.Menu.itemClick}
                data-section-target="loader"
                className="text-gray-600"
              />
              <AnimatedText
                className="cursor-pointer text-gray-400"
                preventUpdate
                data-font-bold
                content="Works"
                data-reveal-duration="0.6"
                data-click-event={EVENTS.Menu.itemClick}
                data-hover-event={EVENTS.Menu.hover}
                data-out-event={EVENTS.Menu.out}
                data-section-target="works"
              />
            </li>

            <li
              className={cn(
                "flex items-center gap-2",
                isOpen ? "pointer-events-auto cursor-default" : undefined,
              )}
              onClick={handleItemClick}
            >
              <AnimatedText
                preventUpdate
                data-font-bold
                content="|"
                data-reveal-duration="0.2"
                data-click-event={EVENTS.Menu.itemClick}
                data-section-target="loader"
                className="text-gray-600"
              />
              <AnimatedText
                className="cursor-pointer text-gray-400"
                preventUpdate
                data-font-bold
                content="Room of Memories"
                data-reveal-duration="0.6"
                data-click-event={EVENTS.Menu.itemClick}
                data-hover-event={EVENTS.Menu.hover}
                data-out-event={EVENTS.Menu.out}
                data-section-target="guestbook"
              />
            </li>
            <li
              className={cn(
                "flex items-center gap-2",
                isOpen ? "pointer-events-auto cursor-default" : undefined,
              )}
              onClick={handleItemClick}
            >
              <AnimatedText
                preventUpdate
                data-font-bold
                content="|"
                data-reveal-duration="0.2"
                data-click-event={EVENTS.Menu.itemClick}
                data-section-target="loader"
                className="text-gray-600"
              />
              <AnimatedText
                className="cursor-pointer text-gray-400"
                preventUpdate
                data-font-bold
                content="Contact"
                data-reveal-duration="0.6"
                data-click-event={EVENTS.Menu.itemClick}
                data-hover-event={EVENTS.Menu.hover}
                data-out-event={EVENTS.Menu.out}
                data-section-target="contact"
              />
            </li>
            <li
              className={cn(
                "flex items-center gap-2",
                isOpen ? "pointer-events-auto cursor-default" : undefined,
              )}
            >
              <AnimatedText
                preventUpdate
                data-font-bold
                content="|"
                data-reveal-duration="0.2"
                data-section-target="loader"
                className="text-gray-700"
              />
            </li>
            <li
              className={cn(
                "flex items-center gap-2",
                isOpen ? "pointer-events-auto cursor-default" : undefined,
              )}
            >
              <AnimatedText
                preventUpdate
                data-font-bold
                content="|"
                data-reveal-duration="0.2"
                data-section-target="loader"
                className="text-gray-700"
              />
            </li>
            <li
              className={cn(
                "flex items-center gap-2",
                isOpen ? "pointer-events-auto cursor-default" : undefined,
              )}
              onClick={handleItemClick}
            >
              <AnimatedText
                preventUpdate
                data-font-bold
                content="|"
                data-reveal-duration="0.2"
                data-section-target="loader"
                className="text-gray-700"
              />
              <AnimatedText
                className="cursor-pointer text-gray-500"
                preventUpdate
                data-font-bold
                content="hi@giulio.me"
                data-contact-cta="email"
                data-reveal-duration="0.6"
                data-hover-event={EVENTS.Menu.hover}
                data-out-event={EVENTS.Menu.out}
              />
            </li>
            <li
              className={cn(
                "flex items-center gap-2",
                isOpen ? "pointer-events-auto cursor-default" : undefined,
              )}
              onClick={handleItemClick}
            >
              <AnimatedText
                preventUpdate
                data-font-bold
                content="]"
                data-reveal-duration="0.2"
                data-section-target="loader"
                className="text-gray-700"
              />
              <AnimatedText
                className="cursor-pointer text-gray-500"
                preventUpdate
                data-font-bold
                content="Telegram"
                data-contact-cta="telegram"
                data-reveal-duration="0.6"
                data-hover-event={EVENTS.Menu.hover}
                data-out-event={EVENTS.Menu.out}
              />
            </li>
          </ul>
        </div>
      </Grid>

      <div className="absolute bottom-10 left-2 w-full hidden md:block">
        <Grid>
          <div className="col-start-1 md:col-start-5 lg:col-start-2 col-span-3">
            <AnimatedText
              className="text-gray-700"
              preventUpdate
              data-text="intro"
              data-font-bold
              tag="h1"
              content="_]]"
            />
            <AnimatedText
              className="text-gray-600"
              preventUpdate
              data-text="intro"
              data-font-bold
              tag="h1"
              content="      ]]["
            />
          </div>
        </Grid>

        <Grid>
          <div className="col-start-2 md:col-start-3 col-span-3 md:col-span-4">
            <AnimatedText
              data-font-bold
              data-text="intro"
              preventUpdate
              className="mt-[5vh] text-gray-500"
              content="I'm a Creative Technologist"
            />
            <AnimatedText
              data-font-bold
              data-text="intro"
              preventUpdate
              className="mt-1 text-gray-300"
              content="They Call Me Giulio"
            />
          </div>
        </Grid>

        <Grid>
          <div className="col-start-1 md:col-start-5 lg:col-start-2 col-span-3">
            <AnimatedText
              preventUpdate
              data-text="intro"
              data-font-bold
              tag="h1"
              className="mt-4 text-gray-700"
              content="   ]]]]_"
            />
          </div>
        </Grid>
      </div>
    </div>
  )
}
